<template>
  <div>
      <v-card class="border-none mt-3">
        <div
          class="chartWrapper"
          :class="{'is-loading': zoomTreeMapLoading}"
        >
          <template v-if="canViewData">
            <div v-if="zoomTreeMap.children && !zoomTreeMap.children.length && !zoomTreeMapLoading">
              <no-data-found
               clas="no-data"
              />
            </div>
            <template v-else>
              <div class="zoomable-treemap-wrapper">
                <svg id="zoomable" />
                <div v-show="loading" class="loading-treemap">
                  <v-progress-circular
                    :size="70"
                    color="primary"
                    indeterminate
                  />
                </div>
              </div>
            </template>
          </template>
          <template v-else>
            <div class="no-data">
              <v-icon size="45px">info</v-icon>
              <span>
              Please fill in your billing info to enable reporting
              <router-link to="/billing">here</router-link>
            </span>
            </div>
          </template>
        </div>
      </v-card>
  </div>
</template>

<script>
import * as d3 from 'd3'
import { scaleLinear, scaleOrdinal } from 'd3-scale'
import { hierarchy, treemap } from 'd3-hierarchy'
import { mapState, mapGetters } from 'vuex'
import { formatNumber, isDescendant, saveSVG } from '../utils'
import NoDataFound from '@/components/Global/NoDataFound'
import { convert } from '@/utils/main_helper'

const d3props = {
  scaleLinear: scaleLinear,
  scaleOrdinal: scaleOrdinal,
  hierarchy: hierarchy,
  key: 'elements',
  treemap: treemap
}
export default {
  name: 'ZoomableTreemap',
  data () {
    return {
      width: 900,
      height: 450,
      whichColor: '',
      folderNameLength: -1,
      activeGroup: null,
      structuredData: {
        name: '',
        children: {}
      },
      timerEnter: 0,
      loading: false
    }
  },
  components: {
    NoDataFound
  },
  computed: {
    ...mapGetters('dashboardServices', ['canViewData']),
    ...mapGetters(['currentOrganization']),
    ...mapState('dashboardServices', ['zoomTreeMap', 'zoomTreeMapLoading', 'unStructured', 'treeMapZoomLevels', 'riskOverviewDate', 'zoomableDetails']),
    ...mapState({
      colorPalette: state => state.colorPaletteServices,
      overallRisk: state => state.dashboardServices.overallRisk
    }),
    colors () {
      return [this.colorPalette.treemap.light, this.colorPalette.treemap.medium, this.colorPalette.treemap.dark]
    },
    getRisks () {
      const rects = this.zoomTreeMap && this.zoomTreeMap.children.map(d => {
        return this.whichColor === 'rectangle' ? d.total_volume
          : d.data_risk_aggregation
      })
      return rects && rects.filter((a, b) => rects.indexOf(a) === b)
    },
    getMinRisk () {
      return this.getRisks && Math.min(...this.getRisks)
    },
    getMaxRisk () {
      return this.getRisks && Math.max(...this.getRisks)
    },
    getColorCouple () {
      const allInts = []
      for (let i = 0; i < this.colors.length; i++) {
        if (i + 1 < this.colors.length) {
          const int = []
          int.push(this.colors[i], this.colors[i + 1])
          allInts.push(int)
        }
      }
      return allInts
    },
    computedColors () {
      const computedColors = []
      for (let i = 0; i < this.getColorCouple.length; i++) {
        for (const color of this.interpolateColors(this.getColorCouple[i][0], this.getColorCouple[i][1], 15)) {
          computedColors.push(color)
        }
      }
      return computedColors
    },
    getContainers () {
      const containers = this.zoomTreeMap && this.zoomTreeMap.children.map(item => item.container_name)
      return containers && containers.filter((a, b) => containers.indexOf(a) === b)
    }
  },
  created () {
    window.addEventListener('mousemove', this.handleMouseMoveGlobal)
  },
  mounted () {
    this.render()
  },
  methods: {
    structureData () {
      const containers = this.getContainers && this.getContainers.map(container => {
        const collections = this.zoomTreeMap?.children.filter(item => item.container_name === container)
        // Adding value property
        collections.forEach(collection => collection.value = collection[d3props.key])
        const totalVolumeSum = collections.map(item => item.total_volume).reduce((total, num) => Number(total) + Number(num))
        return {
          name: container,
          children: collections,
          total_volume_sum: totalVolumeSum
        }
      })
      this.structuredData = {
        name: 'Parent',
        children: containers
      }
      // Sort by total_volume_sum
      function compare (a, b) {
        if (a.total_volume_sum > b.total_volume_sum) {
          return -1
        }
        if (a.total_volume_sum < b.total_volume_sum) {
          return 1
        }
        return 0
      }
      this.structuredData.children.sort(compare)
      return this.structuredData
    },
    render () {
      if (this.zoomTreeMap.children == undefined) return
      if (!this.zoomTreeMap.children.length) return
      this.structureData()
      this.initTooltip()
      const self = this
      const width = 954
      const height = 500
      const x = d3.scaleLinear().rangeRound([0, width])
      const y = d3.scaleLinear().rangeRound([0, height])

      const treemap = data => d3.treemap()
        .tile(tile)
        (d3.hierarchy(data)
          .sum(d => {
            return d.total_volume
          })
          .sort((a, b) => b.value - a.value))

      function tile (node, x0, y0, x1, y1) {
        d3.treemapBinary(node, 0, 0, width, height)
        for (const child of node.children) {
          child.x0 = x0 + child.x0 / width * (x1 - x0)
          child.x1 = x0 + child.x1 / width * (x1 - x0)
          child.y0 = y0 + child.y0 / height * (y1 - y0)
          child.y1 = y0 + child.y1 / height * (y1 - y0)
        }
      }

      const name = d => d.ancestors().reverse().map(d => d.data.name).join('/')

      const format = d3.format(',d')

      const svg = d3.select('#zoomable')
        .attr('viewBox', [0.5, -30.5, width, height + 30])
        .style('font', '10px sans-serif')

      const group = svg.append('g')
        .call(render, treemap(this.zoomTreeMap))

      function render (group, root) {
        svg.transition()
          .attrTween('opacity', () => d3.interpolate(0, 1))
        const node = group
          .selectAll('g')
          .data(root.children.concat(root))
          .join('g')

        node
          .filter(d => d.data.is_folder || d.data.name)
        //  .attr('cursor', 'pointer')
        //  .on('click', d => (Object.prototype.hasOwnProperty.call(d.data, 'children')) ? zoomout(root) : zoomin(d))

        const indicatorColors = []
        node.append('rect')
          .transition()
          .attrTween('opacity', () => d3.interpolate(0, 1))
          .attr('id', d => (d.leafUid = Math.random(0, 18888)).id)
          .attr('fill', d => {
            const backgroundColor = self.getRiskColor(Number(d.data.data_risk_aggregation) || 0, 'risk_aggregation')
            const color = self.getRiskColor(Number(d.data.data_risk_aggregation) || 0, 'indicator')
            indicatorColors.push(color)
            self.$emit('rectangleColors', indicatorColors)
            return d === root ? '#fff' : backgroundColor
          })
          .attr('stroke', '#fff')
          .attr('class', 'square')
          .attr('stroke', '#fff')

        node.append('clipPath')
          .attr('id', d => (d.clipUid = Math.random(1, 19998)))
          .append('use')
          .attr('xlink:href', d => d.leafUid.href)

        node.append('text')
          .attr('clip-path', d => d.clipUid)
          .attr('font-weight', d => d === root ? 'bold' : null)
          .selectAll('tspan')
          .data((d, i, nodes) => {
            const rectWidth = (d.x1 - d.x0) * 954
            const letterWidth = 3.5
            if (Object.prototype.hasOwnProperty.call(d.data, 'name') && Object.prototype.hasOwnProperty.call(d.data, 'children')) {
              var title = self.zoomableDetails.currentDirectory || ''
              if (title) {
                return title.split(/(?=a-zA-Z0-9!@#\$%\^\&*\)\(+=._-)/g).concat(format(self.sumOfDataRiskAggregation(d.data.children)))
              } else return ''
            }
            if (d.data?.container_name?.length * letterWidth > rectWidth) {
              d3.select(nodes[i]).node().remove()
            }
            if (rectWidth > 150) {
              return (d === root ? name(d) : d.data.collection_name).concat('*' + format(d.data.data_risk_aggregation)).split('*')
            } else {
              return (d === root ? name(d) : d.data.collection_name).split(/(?=[A-Z][^A-Z])/g).concat(format(d.data.data_risk_aggregation))
            }
          })
          .join('tspan')
          .attr('x', 3)
          .attr('y', (d, i, nodes) => {
            return `${(i === nodes.length - 1) * 0.3 + 1.1 + i * 0.9}em`
          }
          )
          .attr('fill-opacity', (d, i, nodes) => i === nodes.length - 1 ? 0.7 : null)
          .attr('font-weight', (d, i, nodes) => i === nodes.length - 1 ? 'normal' : null)
          .text(d => d)

        group.call(position, root)
      }

      function position (group, root) {
        group.selectAll('g')
          .attr('transform', d => d === root ? 'translate(0,-30)' : `translate(${x(d.x0) || 0},${y(d.y0) || 0})`)
          .select('rect')
          .attr('width', d => {
            const val = d === root ? width : x(d.x1) - x(d.x0)
            return !isNaN(val) ? val : 0
          })
          .attr('height', d => {
            const val = d === root ? 30 : y(d.y1) - y(d.y0)
            return !isNaN(val) ? val : 0
          })
      }

      // When zooming in, draw the new nodes on top, and fade them in.
      async function zoomin (d) {
        self.loading = true
        self.hideTooltip()
        self.$store.commit('dashboardServices/SET_TREE_MAP_LEVEL', {
          push: true,
          data: {
            id: d.data.collection_id,
            parent_id: self.zoomableDetails.clickedIdContainer,
            data: {
              month: d.data.month || '',
              year: d.data.year || '',
              data_structure_type: d.data.data_structure_type || '',
              parentName: d.data.collection_name
            }
          }
        })
        self.$store.commit('dashboardServices/SET_TREEMAP_DETAIL', { key: 'clickedIdContainer', value: d.data.collection_id })
        const transitionDuration = 750
        self.$store.commit('dashboardServices/SET_TREEMAP_DETAIL', { key: 'zoomLevel', value: self.zoomableDetails.zoomLevel += 1 })
        let dataChild
        if (+d.data.elements >= 0) {
          const { data } = await self.$store.dispatch('dashboardServices/getTreeMapChildren', {
            level: self.zoomableDetails.zoomLevel, // working when in demo mode
            containerId: d.data.collection_id,
            month: d.data.month || '',
            year: d.data.year || '',
            data_structure_type: d.data.data_structure_type || '',
            organizationId: self.currentOrganization.entity_id
          })
          self.loading = false
          dataChild = {
            name: d.data.collection_name,
            children: data
          }
          self.$store.commit('dashboardServices/SET_TREEMAP_DETAIL', { key: 'currentDirectory', value: data?.[0]?.container_name })
        } else dataChild = d.data
        self.activeGroup = dataChild
        const group0 = group.attr('pointer-events', 'none')

        x.domain([d.x0, d.x1])
        y.domain([d.y0, d.y1])

        svg.transition()
          .duration(transitionDuration)
          .call(t => group0.transition(t).remove()
            .call(position, d.parent))
          .attrTween('opacity', () => d3.interpolate(1, 0))
        setTimeout(() => {
          self.$store.commit('dashboardServices/SET_ZOOM_TREE_MAP', {
            name: dataChild.name,
            children: dataChild.children
          })
        }, transitionDuration)
      }

      // When zooming out, draw the old nodes on top, and fade them out.
      async function zoomout (d) {
        if (self.zoomableDetails.zoomLevel === 0) return
        self.hideTooltip()
        self.loading = true
        const findLastClickedContainer = self.treeMapZoomLevels.find(i => i.id === self.zoomableDetails.clickedIdContainer)
        const prevContainer = !findLastClickedContainer.parent_id ? findLastClickedContainer : self.treeMapZoomLevels.find(i => i.id === findLastClickedContainer.parent_id)
        const transitionDuration = 750
        self.$store.commit('dashboardServices/SET_TREEMAP_DETAIL', { key: 'zoomLevel', value: self.zoomableDetails.zoomLevel -= 1 })
        let dataChild
        const { data } = await self.$store.dispatch('dashboardServices/getTreeMapChildren', {
          level: self.zoomableDetails.zoomLevel, // working when in demo mode
          containerId: prevContainer.id,
          month: prevContainer.data.month || '',
          year: prevContainer.data.year || '',
          data_structure_type: prevContainer.data.data_structure_type || '',
          organizationId: self.currentOrganization.entity_id,
          isRoot: self.zoomableDetails.zoomLevel === 0
        })
        self.loading = false
        dataChild = {
          name: self.zoomableDetails.zoomLevel === 0 ? '' : prevContainer.data.parentName,
          children: data
        }
        let currentDirectory = self.zoomableDetails.currentDirectory ?? ''
        if (currentDirectory) {
          currentDirectory = currentDirectory.split('/')
          currentDirectory.pop()
          currentDirectory = currentDirectory.join('/')
        }
        self.$store.commit('dashboardServices/SET_TREEMAP_DETAIL', { key: 'currentDirectory', value: currentDirectory })
        self.activeGroup = dataChild
        self.$store.commit('dashboardServices/SET_TREE_MAP_LEVEL', { cutId: self.zoomableDetails.clickedIdContainer })
        self.$store.commit('dashboardServices/SET_TREEMAP_DETAIL', { key: 'clickedIdContainer', value: prevContainer.id })
        if (self.zoomableDetails.zoomLevel === 0) {
          self.$store.commit('dashboardServices/SET_TREEMAP_DETAIL', { key: 'clickedIdContainer', value: null })
        }
        const group0 = group.attr('pointer-events', 'none')

        x.domain([d.x0, d.x1])
        y.domain([d.y0, d.y1])

        svg.transition()
          .duration(transitionDuration)
          .call(t => group0.transition(t).remove()
            .call(position, d.parent))
          .attrTween('opacity', () => d3.interpolate(1, 0))
        setTimeout(() => {
          self.$store.commit('dashboardServices/SET_ZOOM_TREE_MAP', {
            name: dataChild.name,
            children: dataChild.children
          })
        }, transitionDuration)
      }

      return svg.node()
    },
    sumOfDataRiskAggregation (data) {
      let sum = 0
      data.forEach(d => {
        sum = sum + Number(d.data_risk_aggregation)
      })
      return sum
    },
    interpolateColor (color1, color2, factor) {
      if (arguments.length < 3) {
        factor = 0.5
      }
      const result = color1.slice()
      for (let i = 0; i < 3; i++) {
        result[i] = Math.round(result[i] + factor * (color2[i] - color1[i]))
      }
      let arrayString = ''
      for (const el of result) {
        arrayString += el + ', '
      }
      return `rgb(${arrayString.substring(0, arrayString.length - 2)})`
    },
    interpolateColors (color1, color2, steps) {
      const stepFactor = 1 / (steps - 1)
      const interpolatedColorArray = []

      color1 = color1.match(/\d+/g).map(Number)
      color2 = color2.match(/\d+/g).map(Number)

      for (let i = 0; i < steps; i++) {
        interpolatedColorArray.push(this.interpolateColor(color1, color2, stepFactor * i))
      }
      return interpolatedColorArray
    },
    getRiskColor (currentRiskValue, flag) {
      this.whichColor = flag
      let risksInterval = this.getMaxRisk / this.computedColors.length ?? []
      if (risksInterval == 0) {
        risksInterval = 1
      }
      const intervals = []
      // Creates intervals array of objects
      for (let i = this.getMinRisk; i <= this.getMaxRisk + risksInterval; i += risksInterval) {
        if (i === this.getMinRisk) continue
        const obj = {
          from: Math.round(i !== this.getMinRisk ? i - risksInterval : i),
          to: Math.round(i)
        }
        intervals.push(obj)
      }
      intervals.length && intervals[intervals.length - 1].to++
      // Checks which is the interval array of the currentRiskValue
      for (let i = 0; i < intervals.length; i++) {
        if (currentRiskValue >= intervals[i].from && currentRiskValue < intervals[i].to) {
          return this.computedColors?.[i - 1] ?? this.computedColors?.[0]
        }
      }
      return this.computedColors?.[0]
    },
    initTooltip () {
      let tool
      if (!d3.select('.treeMapTooltip').empty()) {
        tool = d3.select('.treeMapTooltip')
      } else {
        tool = d3.select('body').append('div').attr('class', 'treeMapTooltip')
      }
      this.handleMouseMove(tool)
    },
    handleMouseMove (tool) {
      const treemap = d3.select('#zoomable')
      let targetElement
      treemap.on('mouseover', () => {
        targetElement = d3.event.target.tagName === 'rect' ? d3.event.target : d3.event.target.closest('g')
        const data = targetElement?.__data__?.data
        if (!targetElement || !data?.value) return
        tool.style('display', 'inline-block')
        tool.classed('show', false)
        const rect = targetElement.getBoundingClientRect()
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const tooltipWidth = tool.node().getBoundingClientRect().width - 30 || 0

        // Define position
        let tooltipPositionX, tooltipPositionY
        rect.left + scrollLeft + rect.width + tooltipWidth > window.innerWidth ? tooltipPositionX = 'left' : tooltipPositionX = 'right'
        rect.top + 165 > window.innerHeight ? tooltipPositionY = 'top' : tooltipPositionY = 'bottom'

        // Horizontal move
        if (tooltipPositionX === 'right') {
          let left = rect.left + scrollLeft + rect.width
          if (left < 0) left = 0
          tool.style('left', left + 'px')
        } else {
          let left = rect.left + scrollLeft - tooltipWidth
          if (left < 0) left = 0
          tool.style('left', left + 'px')
        }

        // Vertical move
        if (tooltipPositionY === 'bottom') {
          tool.style('top', rect.top + scrollTop - ((165 - rect.height) / 2) + 'px')
        } else {
          tool.style('top', rect.top + scrollTop + rect.height - 165 + 'px')
        }

        if (this.timerEnter) clearTimeout(this.timerEnter)
        this.timerEnter = setTimeout(() => {
          const tooltipContent = this.renderToolTipContent(data)
          tool.style('display', 'inline-block')
          tool.classed('show', true)
          tool.html('')
          tool.append(tooltipContent)
        }, 300)
      })

      treemap.on('mousedown', () => {
        targetElement = d3.event.target.tagName === 'rect' ? d3.event.target : d3.event.target.closest('g')
        const data = targetElement?.__data__?.data
        if (!targetElement || !data?.value) return
        this.handleTooltipClick(data)
      })
    },
    renderToolTipContent (data) {
      return () => {
        const list = {
          'Collection Name:': data?.collection_name ?? '-',
          'Container Name:': data?.container_name ?? '-',
          'Digital Risk Aggregation Score:': formatNumber(data?.data_risk_aggregation) ?? '-',
          'Collection Size:': data?.size ? convert(data?.size, 'GB', 1000, 1) : '-'
        }
        const wrapper = document.createElement('div')
        const ul = document.createElement('ul')
        const btn = document.createElement('button')
        btn.innerText = 'View Data Inventory'
        btn.addEventListener('click', () => this.handleTooltipClick(data))
        for (const key in list) {
          const li = document.createElement('li')
          li.innerHTML = `<strong>${key}</strong> <span>${list[key]}</span>`
          ul.append(li)
        }
        wrapper.append(ul)
        wrapper.append(btn)
        return wrapper
      }
    },
    handleTooltipClick (data) {
      const moment = require('moment')
      const { container_id, container_name, is_folder, collection_name, collection_id } = data
      this.$router.push({
        name: 'catalog',
        path: '/catalog',
        query: { container_id, collection_id, container_name, collection_name, is_folder, date: moment(this.riskOverviewDate, 'YYYY-MM-DD').format('MM/YYYY') }
      })

      // this.$store.commit('catalogServices/SET_LEVEL', 'collection_name')
      this.hideTooltip()
    },
    handleMouseMoveGlobal (e) {
      if (!isDescendant(document.querySelector('#zoomable'), e.target) && !isDescendant(document.querySelector('.treeMapTooltip'), e.target) && !e.target.classList.contains('treeMapTooltip')) {
        if (this.timerEnter) clearTimeout(this.timerEnter)
        this.hideTooltip()
      }
    },
    hideTooltip () {
      if (this.timerEnter) clearTimeout(this.timerEnter)
      const tool = d3.select('.treeMapTooltip')
      tool.classed('show', false)
      tool.transition().on('end', () => {
        tool.style('display', 'none')
      })
    },
    handleCapture () {
      const svg = d3.select('#zoomable')
      const width = svg.node().getBoundingClientRect().width
      const height = svg.node().getBoundingClientRect().height
      saveSVG(svg, width, height, 'OverviewRisk')
    }
  },
  beforeDestroy () {
    window.removeEventListener('mousemove', this.handleMouseMove)
    window.removeEventListener('mousemove', this.handleMouseMoveGlobal)
    setTimeout(() => {
      this.hideTooltip()
    }, 400)
  },
  destroyed () {
    // d3.select('#zoomable').remove()
  },
  watch: {
    zoomTreeMap: {
      handler () {
        this.$nextTick(() => {
          this.render()
        })
      },
      deep: true
    }
  }
}
</script>

<style lang="scss">
  .v-application--wrap {
    .estimated-risk-chart-card {
      border: none !important;
      border-radius: 0 !important;
    }
  }

  .treeMapTooltip {
      button {
         color: $black-color;
         font-weight: bold;
         font-size: 13px;
         height: 38px;
         line-height: 17px;
         text-align: center;
         letter-spacing: 1.97321px !important;
         text-transform: uppercase;
    }
  }

  .zoomable-treemap-wrapper {
    overflow: hidden;

    .loading-treemap {
      position: absolute;
      background: rgba(255, 255, 255, 0.8);
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  .chartWrapper {
    position: relative;
    overflow: hidden;
    margin: 0 !important;
    &.is-loading {
      &:after {
        background: linear-gradient(90deg, transparent, rgba(108, 81, 81, 0.1), transparent);
        -webkit-animation: loading 1.5s infinite;
        animation: loading 1.5s infinite;
        content: "";
        height: 100%;
        left: 0;
        position: absolute;
        right: 0;
        top: 0;
        transform: translateX(-100%);
        z-index: 1;
      }
      canvas {
        opacity: 0;
      }
    }
    rect[width="0"] {
      ~ text {
        display: none;
      }
    }
  }
  .no-data {
    position: absolute;
    display: flex;
    flex-direction: column;
    width: fit-content;
    height: fit-content;
    padding: 25px;
    span {
      color: $dark-gray-color;
      margin-top: 10px;
    }
    .v-icon {
      color: $dark-gray-color;
    }
  }
</style>
